import {
  createElement,
  lazy,
  useState,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';

/**
 * Lazy-loaded component with an associated preload function.
 */
export interface PreloadableComponent<TProps> {
  component: ComponentType<TProps>;
  preload: () => Promise<unknown>;
}

type DefaultModule<TProps> = {
  default: ComponentType<TProps>;
};

/**
 * Creates a lazy-loaded component that also exposes a preload function.
 *
 * Supports both:
 * - Default exports
 * - Named exports (via the `pick` parameter)
 *
 * Once the chunk is in (a `preload()` that finished, or an earlier visit), the
 * component renders straight away instead of going through `React.lazy`.
 * `React.lazy` suspends on its first render even for a chunk that is already
 * loaded, and the Suspense fallback it shows then stays up for at least 300 ms
 * (React throttles the reveal), so every click onto a preloaded page flashed
 * the loader for nothing.
 */
export function lazyWithPreload<TProps>(
  factory: () => Promise<DefaultModule<TProps>>
): PreloadableComponent<TProps>;

export function lazyWithPreload<TModule, TKey extends keyof TModule, TProps>(
  factory: () => Promise<TModule>,
  pick: TKey
): PreloadableComponent<TProps>;

export function lazyWithPreload<TModule, TKey extends keyof TModule, TProps>(
  factory: () => Promise<TModule>,
  pick?: TKey
): PreloadableComponent<TProps> {
  let loaded: ComponentType<TProps> | null = null;

  const pickComponent = (module: TModule) =>
    (pick
      ? module[pick]
      : (module as DefaultModule<TProps>).default) as ComponentType<TProps>;

  const load = async (): Promise<DefaultModule<TProps>> => {
    const module = await factory();
    loaded = pickComponent(module);
    return { default: loaded };
  };

  const Lazy: LazyExoticComponent<ComponentType<TProps>> = lazy(load);

  // Which one to render is decided once per mount: swapping the element type
  // under a page that is already open would remount it and drop its state.
  const Component: ComponentType<TProps> = (props) => {
    const [Resolved] = useState(
      () => (loaded ?? Lazy) as ComponentType<Record<string, unknown>>
    );
    return createElement(Resolved, props as unknown as Record<string, unknown>);
  };
  Component.displayName = 'LazyWithPreload';

  return {
    component: Component,
    preload: async () => {
      const module = await factory();
      loaded ??= pickComponent(module);
      return module;
    },
  };
}
